/**
 * Card #254 — Holiday & Blackout Dates.
 *
 * PURE date module. No imports, no I/O, no globals, no moment, no lodash —
 * every function here is a deterministic function of its arguments, which
 * means it can be required straight out of node for unit testing:
 *
 *   node -e "const h=require('./app/src/actions/holidays');
 *            console.log(h.holidayDateForYear('thanksgiving', 2026));"
 *   // => 2026-11-26
 *
 * TIMEZONE CONTRACT (this is the whole reason the module exists)
 * -------------------------------------------------------------
 * Every date in and out of this module is a bare 'YYYY-MM-DD' CALENDAR DAY
 * string. Instants / UTC timestamps are deliberately never used or produced.
 *
 * A blackout is a calendar day, not a moment in time. This codebase has three
 * disagreeing notions of "today" — the browser's local zone (all of app/src),
 * a hardcoded America/Denver in the order pipeline (server midDate), and a
 * configurable `tz` setting read by four unrelated files — so anything stored
 * as an instant is off by one day for somebody. Comparing formatted day
 * strings sidesteps all three.
 *
 * `new Date(Date.UTC(...))` is used only as fixed-offset integer arithmetic to
 * get a day-of-week or step a day; it never reads the host timezone, so the
 * results are identical on every machine.
 *
 * MULTI-TENANCY: each client (Michigan, New Mexico, ...) is its own Elastic
 * Beanstalk deployment with its own database, so a blackout calendar is
 * "global" only within one deployment. There is no cross-tenant calendar.
 */

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

// (2026, 11, 26) -> '2026-11-26'. `month` is 1-12, NOT the JS 0-11 convention.
const toDateStr = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

const isDateStr = (v) => typeof v === 'string' && DATE_RE.test(v);

// '2026-11-26' -> { year: 2026, month: 11, day: 26 }; anything else -> null.
const parseDateStr = (str) => {
  if (!isDateStr(str)) { return null; }
  const year = Number(str.slice(0, 4));
  const month = Number(str.slice(5, 7));
  const day = Number(str.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) { return null; }
  return { year, month, day };
};

// Number of days in a (1-12) month. Date.UTC with day 0 rolls back to the last
// day of the previous month, so month here is passed un-decremented.
const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

// 0 = Sunday ... 6 = Saturday. Returns -1 for an unparseable string.
const dayOfWeek = (dateStr) => {
  const p = parseDateStr(dateStr);
  if (!p) { return -1; }
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
};

// Step a calendar day by n days (n may be negative). Pure integer arithmetic on
// a UTC epoch, so no DST anywhere can shift the result.
const addDays = (dateStr, n) => {
  const p = parseDateStr(dateStr);
  if (!p) { return null; }
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day) + (n * 86400000));
  return toDateStr(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
};

/* ------------------------------------------------------------------ *
 * Floating-holiday math
 * ------------------------------------------------------------------ */

/**
 * The nth <weekday> of a month, e.g. the 4th Thursday of November 2026.
 *
 *   month   1-12
 *   weekday 0-6 (0 = Sunday)
 *   n       1-5
 *
 * Walk forward from the 1st to the first matching weekday, then add (n-1) whole
 * weeks. Returns null when the month has no nth occurrence (only reachable for
 * n === 5), which is why the caller must handle null rather than assume 5 works.
 */
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  if (!(month >= 1 && month <= 12) || !(weekday >= 0 && weekday <= 6) || !(n >= 1 && n <= 5)) {
    return null;
  }
  const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const offset = (weekday - firstDow + 7) % 7;
  const day = 1 + offset + ((n - 1) * 7);
  if (day > daysInMonth(year, month)) { return null; }
  return toDateStr(year, month, day);
};

/**
 * The LAST <weekday> of a month, e.g. the last Monday of May (Memorial Day).
 * Walk backwards from the final day of the month. Always resolves.
 */
const lastWeekdayOfMonth = (year, month, weekday) => {
  if (!(month >= 1 && month <= 12) || !(weekday >= 0 && weekday <= 6)) { return null; }
  const lastDay = daysInMonth(year, month);
  const lastDow = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
  const back = (lastDow - weekday + 7) % 7;
  return toDateStr(year, month, lastDay - back);
};

/* ------------------------------------------------------------------ *
 * The preset US federal holiday library
 * ------------------------------------------------------------------ */

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MON = 1;
const THU = 4;

/**
 * The eleven US federal holidays, each carrying the RULE rather than a date, so
 * the calendar stays correct in every future year with no annual re-entry by
 * the admin. Rules are one of:
 *
 *   { type: 'fixed', month, day }        — same month/day every year
 *   { type: 'nth',   month, weekday, n } — nth <weekday> of the month
 *   { type: 'last',  month, weekday }    — last <weekday> of the month
 *
 * NOTE (deliberate omission): no "observed" shifting is applied. When a fixed
 * holiday lands on a weekend the federal observance moves to the adjacent
 * weekday, but GPS delivery days come from the per-ZIP route calendar and are
 * frequently weekday-only anyway. Shifting was not requested for this card; an
 * admin who wants the observed day blocked adds a one-time custom entry for it.
 */
const US_HOLIDAYS = [
  { key: 'newYearsDay', label: "New Year's Day", rule: { type: 'fixed', month: 1, day: 1 } },
  { key: 'mlkDay', label: 'Martin Luther King Jr. Day', rule: { type: 'nth', month: 1, weekday: MON, n: 3 } },
  { key: 'presidentsDay', label: "Presidents' Day", rule: { type: 'nth', month: 2, weekday: MON, n: 3 } },
  { key: 'memorialDay', label: 'Memorial Day', rule: { type: 'last', month: 5, weekday: MON } },
  { key: 'juneteenth', label: 'Juneteenth', rule: { type: 'fixed', month: 6, day: 19 } },
  { key: 'independenceDay', label: 'Independence Day', rule: { type: 'fixed', month: 7, day: 4 } },
  { key: 'laborDay', label: 'Labor Day', rule: { type: 'nth', month: 9, weekday: MON, n: 1 } },
  { key: 'columbusDay', label: 'Columbus Day', rule: { type: 'nth', month: 10, weekday: MON, n: 2 } },
  { key: 'veteransDay', label: 'Veterans Day', rule: { type: 'fixed', month: 11, day: 11 } },
  { key: 'thanksgiving', label: 'Thanksgiving Day', rule: { type: 'nth', month: 11, weekday: THU, n: 4 } },
  { key: 'christmasDay', label: 'Christmas Day', rule: { type: 'fixed', month: 12, day: 25 } },
];

const findHoliday = (key) => {
  for (let i = 0; i < US_HOLIDAYS.length; i++) {
    if (US_HOLIDAYS[i].key === key) { return US_HOLIDAYS[i]; }
  }
  return null;
};

const dateForRule = (rule, year) => {
  if (!rule || !(year >= 1970 && year <= 9999)) { return null; }
  if (rule.type === 'fixed') {
    if (rule.day > daysInMonth(year, rule.month)) { return null; }
    return toDateStr(year, rule.month, rule.day);
  }
  if (rule.type === 'nth') { return nthWeekdayOfMonth(year, rule.month, rule.weekday, rule.n); }
  if (rule.type === 'last') { return lastWeekdayOfMonth(year, rule.month, rule.weekday); }
  return null;
};

/**
 * 'thanksgiving', 2026 -> '2026-11-26'   (4th Thursday of November)
 * 'thanksgiving', 2027 -> '2027-11-25'
 * 'memorialDay',  2026 -> '2026-05-25'   (last Monday of May)
 * Unknown key -> null.
 */
const holidayDateForYear = (key, year) => {
  const h = findHoliday(key);
  return h ? dateForRule(h.rule, year) : null;
};

// [{ key, label, date }] for every preset in one year — used by the admin page
// to show what each toggle actually blocks.
const holidayDatesForYear = (year) => US_HOLIDAYS.map(h => ({
  key: h.key,
  label: h.label,
  date: dateForRule(h.rule, year),
}));

/* ------------------------------------------------------------------ *
 * Stored configuration
 * ------------------------------------------------------------------ */

/**
 * The stored document (one row in `utils`, category 'blackoutdates'):
 *
 *   {
 *     presets:  ['thanksgiving', 'christmasDay'],   // preset keys toggled ON
 *     weekdays: [0, 6],                             // 0=Sun .. 6=Sat, SUBTRACTIVE
 *     entries: [
 *       { id, type: 'single', date: '2026-12-24', label: 'Christmas Eve' },
 *       { id, type: 'range',  startDate: '2026-12-24', endDate: '2026-12-26', label: 'Holiday shutdown' },
 *       { id, type: 'annual', month: 8, day: 3, label: 'Company anniversary' }
 *     ]
 *   }
 *
 * normalizeConfig FAILS OPEN by design: anything missing, null, or the wrong
 * shape degrades to "nothing is blacked out". A config read that returns null
 * (which lodash `get` does NOT replace with a fallback) must never end up
 * blocking every date in the calendar.
 */
const normalizeConfig = (raw) => {
  const cfg = raw && typeof raw === 'object' ? raw : {};
  return {
    presets: Array.isArray(cfg.presets) ? cfg.presets.filter(k => typeof k === 'string') : [],
    weekdays: Array.isArray(cfg.weekdays)
      ? cfg.weekdays.filter(n => Number.isInteger(n) && n >= 0 && n <= 6)
      : [],
    entries: Array.isArray(cfg.entries) ? cfg.entries.filter(e => !!e && typeof e === 'object') : [],
  };
};

const ENTRY_TYPES = ['single', 'range', 'annual'];

/**
 * Shape-check one custom entry. Returns an error string, or '' when valid.
 * Shared by the admin page (before POST) and re-implemented generically on the
 * server; the server never needs to know a holiday from a hole in the ground,
 * it only enforces the same 'YYYY-MM-DD' contract.
 */
const entryError = (entry) => {
  const e = entry && typeof entry === 'object' ? entry : {};
  if (ENTRY_TYPES.indexOf(e.type) === -1) { return 'Choose a blackout type.'; }
  if (e.type === 'single') {
    if (!isDateStr(e.date) || !parseDateStr(e.date)) { return 'Pick a valid date.'; }
    return '';
  }
  if (e.type === 'range') {
    if (!isDateStr(e.startDate) || !parseDateStr(e.startDate)) { return 'Pick a valid start date.'; }
    if (!isDateStr(e.endDate) || !parseDateStr(e.endDate)) { return 'Pick a valid end date.'; }
    // ISO day strings sort lexicographically, so a plain string compare is a
    // correct chronological compare — no parsing, no timezone.
    if (e.endDate < e.startDate) { return 'End date cannot be before the start date.'; }
    return '';
  }
  const month = Number(e.month);
  const day = Number(e.day);
  if (!(month >= 1 && month <= 12)) { return 'Pick a valid month.'; }
  // Leap-year aware without needing a year: Feb 29 is allowed and simply does
  // not match in a common year.
  if (!(day >= 1 && day <= daysInMonth(2024, month))) { return 'Pick a valid day of the month.'; }
  return '';
};

/* ------------------------------------------------------------------ *
 * Evaluation
 * ------------------------------------------------------------------ */

const labelOf = (entry, fallback) => {
  const l = entry && typeof entry.label === 'string' ? entry.label.trim() : '';
  return l || fallback;
};

/**
 * Why is this calendar day blacked out? Returns null when it is not.
 *
 *   describeBlackout('2026-11-26', { presets: ['thanksgiving'] })
 *   // => { kind: 'preset', label: 'Thanksgiving Day', id: 'thanksgiving' }
 *
 * Most-specific-first so the message names the thing the admin actually
 * entered: explicit dates and ranges, then annual repeats, then presets, then
 * the weekday blocklist.
 *
 * The year is taken from the date being tested, so preset and annual holidays
 * resolve correctly for ANY year — 2026, 2031, 2140 — with nothing to
 * pre-compute and no window to fall off the end of.
 */
const describeBlackout = (dateStr, config) => {
  const p = parseDateStr(dateStr);
  if (!p) { return null; }
  const cfg = normalizeConfig(config);

  for (let i = 0; i < cfg.entries.length; i++) {
    const e = cfg.entries[i];
    if (e.type === 'single' && e.date === dateStr) {
      return { kind: 'single', label: labelOf(e, 'Blackout date'), id: e.id };
    }
  }

  for (let i = 0; i < cfg.entries.length; i++) {
    const e = cfg.entries[i];
    if (e.type === 'range' && isDateStr(e.startDate) && isDateStr(e.endDate)
      && dateStr >= e.startDate && dateStr <= e.endDate) {
      return { kind: 'range', label: labelOf(e, 'Blackout period'), id: e.id };
    }
  }

  for (let i = 0; i < cfg.entries.length; i++) {
    const e = cfg.entries[i];
    if (e.type === 'annual' && Number(e.month) === p.month && Number(e.day) === p.day) {
      return { kind: 'annual', label: labelOf(e, 'Annual blackout'), id: e.id };
    }
  }

  for (let i = 0; i < cfg.presets.length; i++) {
    const key = cfg.presets[i];
    if (holidayDateForYear(key, p.year) === dateStr) {
      const h = findHoliday(key);
      return { kind: 'preset', label: h ? h.label : key, id: key };
    }
  }

  const dow = dayOfWeek(dateStr);
  if (cfg.weekdays.indexOf(dow) > -1) {
    return { kind: 'weekday', label: `${WEEKDAY_LABELS[dow]}s are not available`, id: `weekday-${dow}` };
  }

  return null;
};

const isBlackedOut = (dateStr, config) => !!describeBlackout(dateStr, config);

/**
 * Every blacked-out calendar day in [startStr, endStr] inclusive, as
 * [{ date, kind, label }]. Used by the admin page's preview panel and handy for
 * validating the whole calendar in one call. Hard-capped so a fat-fingered
 * range can never spin.
 */
const listBlackoutDatesInRange = (config, startStr, endStr, maxDays = 800) => {
  const out = [];
  if (!isDateStr(startStr) || !isDateStr(endStr) || endStr < startStr) { return out; }
  const cfg = normalizeConfig(config);
  let cur = startStr;
  for (let i = 0; i < maxDays && cur && cur <= endStr; i++) {
    const hit = describeBlackout(cur, cfg);
    if (hit) { out.push({ date: cur, kind: hit.kind, label: hit.label }); }
    cur = addDays(cur, 1);
  }
  return out;
};

module.exports = {
  // primitives
  DATE_RE,
  toDateStr,
  isDateStr,
  parseDateStr,
  daysInMonth,
  dayOfWeek,
  addDays,
  // floating-holiday math
  nthWeekdayOfMonth,
  lastWeekdayOfMonth,
  // preset library
  US_HOLIDAYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT,
  holidayDateForYear,
  holidayDatesForYear,
  // config + evaluation
  ENTRY_TYPES,
  normalizeConfig,
  entryError,
  describeBlackout,
  isBlackedOut,
  listBlackoutDatesInRange,
};
